import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { object as PropObject } from 'prop-types';

import { LoggableError } from '../../../common/models';
import { ErrorLogger } from './ErrorLogger';

interface ErrorMessageProps extends RouteComponentProps<any> {
  errorLogger: ErrorLogger;
  loading: boolean;
  onLoading(loading: boolean): void;
}
interface ErrorMessageState {
  errors: LoggableError[];
  validationMessages: string[];
}

const ValidationMessages = ({ messages }: { messages: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }

  return (
    <ul>
      {messages.map((message, i) => <li key={message + '_' + i}>{message}</li> )}
    </ul>
  );
};

const ErrorStack = ({ errors, showDetails }: { errors: LoggableError[]; showDetails: boolean }) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div>
      <p>
        The system is not able to complete the action you are trying to perform. Please reload the page and try
        again. If the problem persists, please contact{' '}<a href="mailto:gsit@ucdavis.edu">tech support</a>.
      </p>

      {
        errors
          .filter(error => error.message && error.message !== 'Error')
          .map((error, i) => (
          <dl className="dl-horizontal" key={error.message + '_' + i}>
            <dt>Message</dt>
            <dd>{error.message}</dd>
            {error.fileName && showDetails ? <dt>Template</dt> : null}
            {error.fileName && showDetails ? <dd>{error.fileName}</dd> : null}
            {error.lineNumber && showDetails ? <dt>Line Number</dt> : null}
            {error.lineNumber && showDetails ? <dd>{error.lineNumber}</dd> : null}
            {error.stack && showDetails ? <dt>Stack Trace</dt> : null}
            {error.stack && showDetails ? <dd><pre>{error.stack}</pre></dd> : null}
          </dl>
        ))
      }
    </div>
  );
};

export class ErrorMessageComponent extends React.Component<ErrorMessageProps, ErrorMessageState> {
  static contextTypes = { router: PropObject };

  close = () => this.props.errorLogger.clearErrors();

  constructor(props: ErrorMessageProps) {
    super(props);

    const { errors, validationMessages } = this.props.errorLogger;

    this.state = { errors, validationMessages };
  }

  componentDidMount() {
    this.props.errorLogger.registerComponent(this.context.router.history, this);
  }

  componentWillUpdate(nextProps: ErrorMessageProps, nextState: ErrorMessageState) {
    if ( nextState.errors.length || nextState.validationMessages.length ) {
      if (this.props.loading) {
        this.props.onLoading(false);
      }

      if (nextProps.location !== this.props.location) {
        this.props.errorLogger.clearErrors();
      }
    }
  }

  onLoading = (loading: boolean) => this.props.onLoading(loading);

  render() {
    if ( this.state.errors.length === 0 && this.state.validationMessages.length === 0) {
      return null;
    }

    const showDetails = this.props.errorLogger.shortEnv !== 'prod';

    return (
      <section className="panel panel-error">
        <header className="panel-heading">
          <a onClick={this.close} className="close">&times;</a>

          <i className="fa fa-minus-circle"/>

          <h2 className="alert-heading">Oops, we've encountered an error!</h2>
        </header>

        <div className="panel-body">
          {/* Validation Errors that should be displayed (400) */}
          <ValidationMessages messages={this.state.validationMessages}/>

          {/* Client and Server Errors (500 = Server Error) */}
          <ErrorStack errors={this.state.errors} showDetails={showDetails}/>
        </div>
      </section>
    );
  }
}

export const ErrorMessage = withRouter(ErrorMessageComponent);



// WEBPACK FOOTER //
// ./src/gs-core/client/react/error/ErrorMessage.tsx